// Shared "print a customer-facing voucher card" helper, used by both the
// Voucher list (per-row Print) and the Voucher detail page. The card carries a
// QR of the code so a cashier can scan it at the POS (BillDiscountModal → Scan)
// to apply the discount. Kept framework-free (plain DOM + a print window) so it
// works identically from any caller.

import QRCode from 'qrcode';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { formatVoucherDay, formatVoucherValue } from './format';
import type { Voucher } from './types';

export type PrintVoucherResult = { ok: true } | { ok: false; reason: 'popup-blocked' | 'error' };

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export async function printVoucher(voucher: Voucher, currency: CurrencyCode, timeZone: string): Promise<PrintVoucherResult> {
  try {
    const qr = await QRCode.toDataURL(voucher.code, { width: 240, margin: 1 });
    const win = window.open('', '_blank', 'width=440,height=680');
    if (!win) return { ok: false, reason: 'popup-blocked' };

    const valueLabel = escapeHtml(formatVoucherValue(voucher, currency));
    const code = escapeHtml(voucher.code);
    const minOrder = voucher.min_order_value > 0
      ? `Min order ${escapeHtml(formatPaise(voucher.min_order_value, currency))}`
      : 'No minimum order';
    const validity = voucher.expires_at
      ? `Valid till ${escapeHtml(formatVoucherDay(voucher.expires_at, timeZone, true))}`
      : 'No expiry date';
    const description = voucher.description ? escapeHtml(voucher.description) : '';

    win.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Voucher ${code}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; padding: 24px; color: #111827; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .voucher { width: 340px; margin: 0 auto; border: 2px dashed #6366f1; border-radius: 16px; padding: 24px; text-align: center; }
  .tag { display: inline-block; font-size: 11px; letter-spacing: .12em; text-transform: uppercase; font-weight: 700; color: #6d28d9; background: #ede9fe; padding: 4px 10px; border-radius: 999px; }
  .value { font-size: 34px; font-weight: 800; margin: 14px 0 4px; color: #4f46e5; }
  .code { font-family: ui-monospace, 'Courier New', monospace; font-size: 22px; font-weight: 700; letter-spacing: .12em; background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px 12px; margin: 12px 0; word-break: break-all; }
  .qr { margin: 8px auto 4px; }
  .meta { font-size: 12px; color: #6b7280; margin-top: 6px; }
  .desc { font-size: 13px; color: #374151; margin-top: 10px; font-style: italic; }
  .hint { font-size: 11px; color: #9ca3af; margin-top: 14px; }
  @media print { body { padding: 0; } .voucher { border-color: #6366f1; } }
</style>
</head>
<body>
  <div class="voucher">
    <span class="tag">Discount Voucher</span>
    <div class="value">${valueLabel}</div>
    ${description ? `<div class="desc">${description}</div>` : ''}
    <div class="code">${code}</div>
    <img class="qr" src="${qr}" width="180" height="180" alt="Voucher QR" />
    <div class="meta">${minOrder}</div>
    <div class="meta">${validity}</div>
    <div class="hint">Scan or enter this code at checkout to redeem.</div>
  </div>
  <script>
    window.onload = function () { window.focus(); window.print(); };
    window.onafterprint = function () { window.close(); };
  </script>
</body>
</html>`);
    win.document.close();
    return { ok: true };
  } catch {
    return { ok: false, reason: 'error' };
  }
}
